import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function useDriverCharges() {
  const { baseurl, token, setIsUnauthorised } = useGlobalContext();

  const [chargeRows, setChargeRows] = useState([]);
  const [chargesLoading, setChargesLoading] = useState(false);

  /* ================= GET ALL DRIVER CHARGES ================= */

  const fetchDriverCharges = useCallback(
    async (silent = false) => {
      if (!silent) setChargesLoading(true);

      try {
        const res = await axios.post(
          `${baseurl}/api/Transactions/v1.0/getDriverCharges`,
          { driverCode: "" },
          { headers: { Authorization: `Basic ${token}` } }
        );

        if (res.data.responseCode === "00") {
         const normalized = (res.data.data || []).map((g) => {
  const charges = g.charges || [];

  return {
    driverCode: g.driverCode,
    name: g.name || "",     
    teamName: g.teamName || "",
    per_day: charges.find((c) => c.type === "per_day") || null,
    per_order: charges.find((c) => c.type === "per_order") || null,
  };
});

          setChargeRows(normalized);
        } else if (res.data.responseCode === "401") {
          setIsUnauthorised(true);
        } else {
          toast.error(
            res.data.responseMessage || "Failed to fetch charges"
          );
        }
      } catch {
        toast.error("Error fetching driver charges");
      } finally {
        if (!silent) setChargesLoading(false);
      }
    },
    [baseurl, token, setIsUnauthorised]
  );

  /* ================= GET CHARGES FOR SINGLE DRIVER ================= */

  const fetchChargesForDriverApi = async (
    driverCode,
    setExisting,
    setLoading
  ) => {
    if (!driverCode) return;

    setLoading(true);
    try {
      const res = await axios.post(
        `${baseurl}/api/Transactions/v1.0/getDriverCharges`,
        { driverCode },
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        const group = (res.data.data || [])[0];
        const charges = group?.charges || [];

        const perDay =
          charges.find((c) => c.type === "per_day") || null;
        const perOrder =
          charges.find((c) => c.type === "per_order") || null;

        setExisting({
          per_day: {
            id: perDay?.id ?? null,
            amount: perDay?.amount?.toString?.() ?? "",
          },
          per_order: {
            id: perOrder?.id ?? null,
            amount: perOrder?.amount?.toString?.() ?? "",
          },
        });
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(
          res.data.responseMessage || "Failed to fetch charges"
        );
      }
    } catch {
      toast.error("Error fetching driver charges");
    } finally {
      setLoading(false);
    }
  };

 /* ================= ADD / UPDATE DRIVER CHARGES ================= */

const saveDriverChargeApi = async ({
  adminId,
  driverCode,
  perDayAmount,
  perOrderAmount,
}) => {
  if (!adminId || !driverCode) {
    toast.error("Missing required fields");
    return null;
  }

  const payload = {
    adminId,
    driverCode,
    type1: "per_day",
    type1Amount: String(perDayAmount ?? 0),
    type2: "per_order",
    type2Amount: String(perOrderAmount ?? 0),
  };

  try {
    const res = await axios.post(
      `${baseurl}/api/Transactions/v1.0/addDriverCharges`,
      payload,
      {
        headers: { Authorization: `Basic ${token}` },
      }
    );

    if (res.data?.responseCode === "00") {
      toast.success(res.data.responseMessage || "Driver charges saved");

      return res;
    } else if (res.data?.responseCode === "401") {
      setIsUnauthorised(true);
      return null;
    } else {
      toast.error(res.data?.responseMessage || "Failed to save charges");
      return null;
    }
  } catch (error) {
    toast.error("Error saving driver charges");
    return null;
  }
};

  return {
    chargeRows,
    chargesLoading,
    fetchDriverCharges,
    fetchChargesForDriverApi,
    saveDriverChargeApi,
  };
}
